import React, { useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n";
import { api } from "../api";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch {
      alert("Error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black text-white pt-32 pb-32 min-h-screen">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }} className="md:col-span-5">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-6">{t.contact.title}</div>
          <h1 className="font-black text-5xl md:text-6xl leading-[0.9] tracking-tight uppercase mb-8">
            Write to <span className="text-blood">the atelier.</span>
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-md mb-12">{t.contact.subtitle}</p>

          <div className="space-y-6 text-sm">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">Atelier</div>
              <div>Gustavia, Saint Barthélemy<br/>97133, F.W.I.</div>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">Email</div>
              <a href="mailto:atelier@beaudoin.com" className="link-hover">atelier@beaudoin.com</a>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">Press</div>
              <a href="mailto:press@beaudoin.com" className="link-hover">press@beaudoin.com</a>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="md:col-span-7 md:border-l md:border-white/10 md:pl-16">
          {sent ? (
            <div data-testid="contact-success" className="text-center py-20">
              <div className="font-gothic text-blood text-6xl mb-6">✟</div>
              <p className="text-xl text-bone">{t.contact.thanks}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
              <Field label={t.contact.name} value={form.name}
                onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
              <Field type="email" label={t.contact.email} value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
              <Field label={t.contact.message} value={form.message}
                onChange={(v) => setForm({ ...form, message: v })} multiline testid="contact-message" />
              <button type="submit" data-testid="contact-submit" disabled={loading}
                className="bg-blood text-white px-12 py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors disabled:opacity-50">
                {loading ? "..." : t.contact.send}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-3">{label}</label>
      {multiline ? (
        <textarea data-testid={testid} required value={value} onChange={(e) => onChange(e.target.value)} rows={5}
          className="w-full bg-transparent border-b border-white/20 focus:border-blood outline-none py-3 text-base resize-none transition-colors" />
      ) : (
        <input data-testid={testid} required type={type} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-white/20 focus:border-blood outline-none py-3 text-base transition-colors" />
      )}
    </div>
  );
}
